import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmDialog } from '../components/ConfirmDialog';
import MediaInteractionModal from '../components/MediaInteractionModal';
import api from '../utils/api';
import './CastCrew.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const getMediaUrl = (key: string | null | undefined): string | null => {
  if (!key) return null;
  if (key.startsWith('http')) return key;
  return `${BACKEND_URL}/uploads/${key}`;
};

interface RoleAssignment {
  member_id: number;
  role_type: 'character' | 'crew' | 'extra';
  role_name: string;
  role_description?: string;
  joined_at: string;
}

interface MediaItem {
  id: number;
  file_name: string;
  media_type: string;
}

interface Collaborator {
  id: number;
  media_id: number;
  role_name: string;
  user_id: number;
  user_name: string;
  user_dp: string | null;
  user_roles: string[];
  user_city: string | null;
  user_state: string | null;
}

interface Member {
  user_id: number;
  name: string;
  email: string;
  city?: string;
  state?: string;
  roles?: string[];
  profile_picture?: string | null;
  is_owner: boolean;
  photos: MediaItem[];
  videos: MediaItem[];
  assignments: RoleAssignment[];
}

// A character from the story that may or may not be assigned
interface StoryCharacter {
  id: number;
  name: string;
  description?: string;
  assignedMember?: {
    user_id: number;
    name: string;
    profile_picture?: string | null;
  } | null;
}

// ─────────────────────────────────────────────────────────────
/* ============================================================
   Page
   ============================================================ */

const CastCrewPage: React.FC = () => {
  const navigate = useNavigate();
  const { storyId } = useParams<{ storyId: string }>();
  const { user } = useAuth();
  const { confirm, confirmDialog } = useConfirmDialog();

  const [members, setMembers]         = useState<Member[]>([]);
  const [storyCharacters, setStoryCharacters] = useState<StoryCharacter[]>([]);
  const [isOwner, setIsOwner]         = useState(false);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [message, setMessage]         = useState('');
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  // Tabs: 'all' | 'characters' | 'crew' | 'extras'
  // 'characters' now shows the merged character roster (story characters + assignment status)
  const [activeTab, setActiveTab]     = useState<'all' | 'characters' | 'crew' | 'extras'>('all');
  const [dpErrors, setDpErrors]       = useState<Set<number>>(new Set());

  const handleDpError = (userId: number) =>
    setDpErrors(prev => new Set(prev).add(userId));

  useEffect(() => {
    loadMembers();
    loadStoryCharacters();
  }, [storyId]);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/groups/${storyId}/cast-crew`);
      setMembers(response.data.members);
      setIsOwner(response.data.isOwner);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const loadStoryCharacters = async () => {
    try {
      const chRes = await api.get(`/stories/${storyId}/characters`);
      const chars: { id: number; name: string; description?: string }[] = chRes.data.characters || [];

      const memberRes = await api.get(`/groups/${storyId}/cast-crew`);
      const groupMembers: Member[] = memberRes.data.members || [];

      const assignmentMap = new Map<string, { user_id: number; name: string; profile_picture?: string | null }>();
      for (const member of groupMembers) {
        for (const assignment of member.assignments) {
          if (assignment.role_type === 'character') {
            assignmentMap.set(assignment.role_name.toUpperCase(), {
              user_id: member.user_id,
              name: member.name,
              profile_picture: member.profile_picture,
            });
          }
        }
      }

      const enriched: StoryCharacter[] = chars.map(ch => ({
        ...ch,
        assignedMember: assignmentMap.get(ch.name.toUpperCase()) || null,
      }));

      setStoryCharacters(enriched);
    } catch {
      // silent — characters are optional
    }
  };

  const getFilteredMembers = () => {
    if (activeTab === 'all') return members;
    const roleType = activeTab === 'crew' ? 'crew' : 'extra';
    return members.filter(m => m.assignments.some(a => a.role_type === roleType));
  };

  const handleRemoveMember = async (memberId: number, memberName: string, roleName: string) => {
    const approved = await confirm({
      title: 'Remove Role',
      message: `Remove ${memberName} from the role of "${roleName}"?`,
      confirmLabel: 'Remove',
      cancelLabel: 'Keep',
      danger: true,
    });
    if (!approved) return;
    try {
      await api.delete(`/groups/${storyId}/members/${memberId}`);
      setMessage('Role removed successfully');
      setTimeout(() => setMessage(''), 2000);
      loadMembers();
      loadStoryCharacters();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove role');
    }
  };

  const getRoleTypeIcon = (roleType: string) =>
    roleType === 'character' ? '🎭' : roleType === 'crew' ? '🎬' : '👥';

  if (loading) {
    return (
      <div className="cast-crew-container">
        <div className="loading">Loading cast &amp; crew…</div>
      </div>
    );
  }

  const isCharactersTab = activeTab === 'characters';
  const currentMembers  = isCharactersTab ? [] : getFilteredMembers();

  const tabCounts = {
    all:        members.length,
    // For the characters tab label, show story character count (total defined characters)
    characters: storyCharacters.length,
    crew:       members.filter(m => m.assignments.some(a => a.role_type === 'crew')).length,
    extras:     members.filter(m => m.assignments.some(a => a.role_type === 'extra')).length,
  };

  return (
    <div className="cast-crew-container">
      {confirmDialog}

      <div className="cast-crew-header">
        <button onClick={() => navigate(`/group/${storyId}`)} className="back-btn">
          ← Back to Group
        </button>
        <h1>Cast &amp; Crew</h1>
      </div>

      {error   && <div className="error-message">{error}</div>}
      {message && <div className="success-message">{message}</div>}

      <div className="cast-crew-tabs">
        {(['all', 'characters', 'crew', 'extras'] as const).map((tab) => {
          const count = tabCounts[tab];
          const label =
            tab === 'all'        ? 'All Members' :
            tab === 'characters' ? '🎭 Characters' :
            tab.charAt(0).toUpperCase() + tab.slice(1);
          return (
            <button
              key={tab}
              className={`tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      {/* ── Characters Tab — merged roster ── */}
      {isCharactersTab && (
        <div className="members-section">
          {storyCharacters.length === 0 ? (
            <div className="no-members">
              <p>No characters defined in this story yet.</p>
              {isOwner && (
                <button
                  onClick={() => navigate(`/story/${storyId}`)}
                  className="add-members-btn"
                >
                  Go to Story Editor
                </button>
              )}
            </div>
          ) : (
            <div className="character-full-roster">
              <div className="character-full-roster-header">
                <span className="character-full-roster-summary">
                  {storyCharacters.filter(c => c.assignedMember).length} assigned
                  {' · '}
                  {storyCharacters.filter(c => !c.assignedMember).length} open
                </span>
              </div>
              <div className="character-full-roster-grid">
                {storyCharacters.map(ch => {
                  const isAssigned = !!ch.assignedMember;
                  const dpUrl = ch.assignedMember ? getMediaUrl(ch.assignedMember.profile_picture) : null;

                  return (
                    <div
                      key={ch.id}
                      className={`character-full-card${isAssigned ? ' assigned' : ' unassigned'}`}
                    >
                      {isAssigned && <div className="character-full-assigned-ribbon">Assigned</div>}

                      <div className="character-full-avatar">
                        {isAssigned ? (
                          dpUrl
                            ? <img src={dpUrl} alt={ch.assignedMember!.name} />
                            : <span>{ch.assignedMember!.name.charAt(0).toUpperCase()}</span>
                        ) : (
                          <span className="character-full-avatar-empty">?</span>
                        )}
                      </div>

                      <div className="character-full-character-name">{ch.name}</div>
                      {ch.description && (
                        <div className="character-full-character-desc">{ch.description}</div>
                      )}

                      <div className={`character-full-status${isAssigned ? ' is-assigned' : ' is-open'}`}>
                        {isAssigned ? (
                          <>
                            <span className="cfull-dot cfull-dot-assigned" />
                            <span>Played by <strong>{ch.assignedMember!.name}</strong></span>
                          </>
                        ) : (
                          <>
                            <span className="cfull-dot cfull-dot-open" />
                            <span>Not assigned to anybody yet</span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── All / Crew / Extras tabs ── */}
      {!isCharactersTab && (
        <div className="members-section">
          {currentMembers.length === 0 ? (
            <div className="no-members">
              <p>No members found.</p>
              {isOwner && (
                <button
                  onClick={() => navigate(`/auditions/${storyId}`)}
                  className="add-members-btn"
                >
                  Add Members
                </button>
              )}
            </div>
          ) : (
            <div className="members-grid">
              {currentMembers.map((member) => {
                const dpUrl   = getMediaUrl(member.profile_picture);
                const showImg = dpUrl && !dpErrors.has(member.user_id);
                const visibleAssignments =
                  activeTab === 'all'    ? member.assignments :
                  activeTab === 'crew'   ? member.assignments.filter(a => a.role_type === 'crew') :
                                           member.assignments.filter(a => a.role_type === 'extra');

                return (
                  <div key={member.user_id} className="member-card">
                    {member.is_owner && <div className="owner-badge">Owner</div>}

                    <div
                      className="member-avatar-large"
                      onClick={() => setSelectedMember(member)}
                    >
                      {showImg
                        ? <img src={dpUrl!} alt={member.name} onError={() => handleDpError(member.user_id)} />
                        : <div className="avatar-placeholder">{member.name.charAt(0).toUpperCase()}</div>
                      }
                    </div>

                    <div className="member-info">
                      <h3 onClick={() => setSelectedMember(member)}>{member.name}</h3>
                      <div className="member-role-assignments">
                        {visibleAssignments.map((assignment) => (
                          <div key={assignment.member_id} className="role-assignment-item">
                            <div className="member-role">
                              <span className="role-icon">{getRoleTypeIcon(assignment.role_type)}</span>
                              <span className="role-name">{assignment.role_name}</span>
                            </div>
                            {assignment.role_description && (
                              <p className="role-description">{assignment.role_description}</p>
                            )}
                            {isOwner && !member.is_owner && (
                              <button
                                onClick={() => handleRemoveMember(
                                  assignment.member_id,
                                  member.name,
                                  assignment.role_name
                                )}
                                className="remove-role-btn-small"
                              >
                                Remove this role
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      {member.roles && member.roles.length > 0 && (
                        <div className="member-roles-tags">
                          {member.roles.slice(0, 3).map((role, idx) => (
                            <span key={idx} className="role-tag-tiny">{role}</span>
                          ))}
                        </div>
                      )}
                      {member.city && (
                        <p className="member-location">{member.city}, {member.state}</p>
                      )}
                    </div>

                    <div className="member-actions">
                      <button
                        onClick={() => setSelectedMember(member)}
                        className="view-profile-btn-member"
                      >
                        View Profile
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {selectedMember && (
        <MemberProfileModal
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
        />
      )}
    </div>
  );
};

/* ============================================================
   Member Profile Modal
   ============================================================ */

interface MemberProfileModalProps {
  member: Member;
  onClose: () => void;
}

const MemberProfileModal: React.FC<MemberProfileModalProps> = ({ member, onClose }) => {
  const [activeMediaTab, setActiveMediaTab] = useState<'photos' | 'videos'>('photos');
  const [dpError, setDpError] = useState(false);
  const [lightbox, setLightbox] = useState<{ url: string; isVideo: boolean; mediaId: number } | null>(null);
  const [collabMap, setCollabMap]         = useState<Record<number, Collaborator[]>>({});
  const [collabLoading, setCollabLoading] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (lightbox) setLightbox(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, onClose]);

  const fetchCollabs = useCallback(async (mediaId: number) => {
    if (collabMap[mediaId] !== undefined) return;
    setCollabLoading(prev => ({ ...prev, [mediaId]: true }));
    try {
      const res = await api.get(`/media/${mediaId}/collaborators`);
      setCollabMap(prev => ({ ...prev, [mediaId]: res.data.collaborators }));
    } catch {
      setCollabMap(prev => ({ ...prev, [mediaId]: [] }));
    } finally {
      setCollabLoading(prev => ({ ...prev, [mediaId]: false }));
    }
  }, [collabMap]);

  const openLightbox = (url: string, isVideo: boolean, mediaId: number) => {
    setLightbox({ url, isVideo, mediaId });
    if (mediaId > 0) fetchCollabs(mediaId);
  };

  const profileDpUrl   = getMediaUrl(member.profile_picture);
  const showProfileImg = profileDpUrl && !dpError;
  const activeMedia    = activeMediaTab === 'photos' ? member.photos : member.videos;

  const getRoleTypeIcon = (roleType: string) =>
    roleType === 'character' ? '🎭' : roleType === 'crew' ? '🎬' : '👥';

  return (
    <>
      <div className="modal-overlay" style={{ zIndex: 200 }} onClick={onClose}>
        <div className="modal-content-large member-modal" onClick={(e) => e.stopPropagation()}>
          <button onClick={onClose} className="modal-close">✕</button>

          <div className="member-profile-header">
            <div
              className={`profile-avatar-xlarge${!showProfileImg ? ' placeholder' : ''}`}
              onClick={() => showProfileImg && openLightbox(profileDpUrl!, false, -1)}
              style={{ cursor: showProfileImg ? 'zoom-in' : 'default' }}
            >
              {showProfileImg
                ? <img src={profileDpUrl!} alt={member.name} onError={() => setDpError(true)} />
                : member.name.charAt(0).toUpperCase()
              }
            </div>
            <h2>{member.name}</h2>
            <p className="profile-email">{member.email}</p>
          </div>

          <div className="member-profile-roles">
            <h3>Role Assignments</h3>
            {member.assignments.map((assignment) => (
              <div key={assignment.member_id} className="role-assignment-card">
                <span className="role-icon-large">{getRoleTypeIcon(assignment.role_type)}</span>
                <div>
                  <h4>{assignment.role_type.charAt(0).toUpperCase() + assignment.role_type.slice(1)}</h4>
                  <p className="role-name-large">{assignment.role_name}</p>
                  {assignment.role_description && (
                    <p className="role-desc-small">{assignment.role_description}</p>
                  )}
                  <p className="joined-date-small">
                    Joined {new Date(assignment.joined_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="member-profile-details">
            {member.roles && member.roles.length > 0 && (
              <div className="profile-detail-item">
                <span className="detail-label">Roles</span>
                <div className="detail-value roles-display">
                  {member.roles.map((role, i) => (
                    <span key={i} className="role-badge-modal">{role}</span>
                  ))}
                </div>
              </div>
            )}
            {member.city && (
              <div className="profile-detail-item">
                <span className="detail-label">Location</span>
                <span className="detail-value">{member.city}, {member.state}</span>
              </div>
            )}
          </div>

          {(member.photos.length > 0 || member.videos.length > 0) && (
            <div className="member-profile-media">
              <div className="media-tabs-modal">
                <button
                  className={`media-tab-modal ${activeMediaTab === 'photos' ? 'active' : ''}`}
                  onClick={() => setActiveMediaTab('photos')}
                >Photos ({member.photos.length})</button>
                <button
                  className={`media-tab-modal ${activeMediaTab === 'videos' ? 'active' : ''}`}
                  onClick={() => setActiveMediaTab('videos')}
                >Videos ({member.videos.length})</button>
              </div>

              <div className="media-gallery-modal">
                {activeMedia.length > 0 ? (
                  <div className="media-grid-modal">
                    {activeMedia.map((item) => {
                      const url = getMediaUrl(item.file_name);
                      if (!url) return null;
                      const isVideo = item.media_type === 'video';
                      return (
                        <div
                          key={item.id}
                          className={`media-item-modal${isVideo ? ' video-item-modal' : ''}`}
                          onClick={() => openLightbox(url, isVideo, item.id)}
                          style={{ cursor: 'zoom-in' }}
                        >
                          {isVideo ? (
                            <>
                              <video src={url} preload="metadata" />
                              <span className="us-play-icon">▶</span>
                            </>
                          ) : (
                            <img
                              src={url}
                              alt="Photo"
                              onError={(e) => {
                                e.currentTarget.parentElement!.style.display = 'none';
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-media-modal">
                    <p>No {activeMediaTab} yet.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {lightbox && (
        <MediaInteractionModal
          mediaId={lightbox.mediaId}
          src={lightbox.url}
          isVideo={lightbox.isVideo}
          collaborators={(collabMap[lightbox.mediaId] || []).map(c => ({
            ...c,
            user_dp: getMediaUrl(c.user_dp),
          }))}
          collaboratorsLoading={Boolean(collabLoading[lightbox.mediaId])}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
};

export default CastCrewPage;

